/**
 * Política de Privacidade — placeholder MVP com seções LGPD obrigatórias.
 *
 * Substituir pelo texto definitivo do jurídico antes de captação real
 * de dados pessoais em produção.
 */
import Head from "next/head";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { Brand } from "@/components/Brand";
import { SmartBackLink } from "@/components/SmartBackLink";

export default function PrivacidadePage() {
  const { t } = useTranslation();
  const lastUpdate = "08 de maio de 2026";

  return (
    <>
      <Head>
        <title>{t("legal.privacy.metaTitle", "Política de Privacidade — Miller Platform")}</title>
        <meta name="description" content={t("legal.privacy.metaDesc", "Como a Miller Platform trata seus dados pessoais.")} />
      </Head>

      <div className="min-h-screen bg-app-bg">
        <header className="border-b border-app bg-app-surface">
          <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
            <Link href="/" className="hover:opacity-80 transition-opacity">
              <Brand />
            </Link>
            <SmartBackLink className="text-sm text-app-2 hover:text-app-1 font-medium">
              {t("legal.back", "Voltar")}
            </SmartBackLink>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-6 py-12">
          <p className="text-xs font-semibold text-app-3 uppercase tracking-widest mb-2">
            {t("legal.privacy.eyebrow", "PRIVACIDADE & LGPD")}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-app-1 tracking-tight mb-3">
            {t("legal.privacy.title", "Política de Privacidade")}
          </h1>
          <p className="text-sm text-app-3 mb-10">
            {t("legal.privacy.lastUpdate", "Última atualização")}: {lastUpdate}
          </p>

          <div className="space-y-6 text-app-2 leading-relaxed">
            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                1. {t("legal.privacy.s1.title", "Quem somos")}
              </h2>
              <p>
                {t("legal.privacy.s1.body", "A Miller (CNPJ a definir) é a controladora dos dados pessoais coletados na plataforma millersolutions.com.br. Para dúvidas, contate")}{" "}
                <a href="mailto:dpo@millersolutions.com.br" className="text-app-1 underline hover:opacity-80">
                  dpo@millersolutions.com.br
                </a>
                .
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                2. {t("legal.privacy.s2.title", "Quais dados coletamos")}
              </h2>
              <ul className="list-disc list-inside space-y-1.5">
                <li>{t("legal.privacy.s2.i1", "Dados de identificação: nome, e-mail, foto de perfil")}</li>
                <li>{t("legal.privacy.s2.i2", "Dados de empresa: CNPJ, razão social, endereço")}</li>
                <li>{t("legal.privacy.s2.i3", "Dados de uso: páginas acessadas, ações realizadas (auditoria interna)")}</li>
                <li>{t("legal.privacy.s2.i4", "Dados técnicos: IP, navegador, sistema operacional")}</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                3. {t("legal.privacy.s3.title", "Por que coletamos")}
              </h2>
              <ul className="list-disc list-inside space-y-1.5">
                <li>{t("legal.privacy.s3.i1", "Autenticação e controle de acesso")}</li>
                <li>{t("legal.privacy.s3.i2", "Personalização de tema e idioma")}</li>
                <li>{t("legal.privacy.s3.i3", "Auditoria de segurança e prevenção de fraude")}</li>
                <li>{t("legal.privacy.s3.i4", "Suporte ao usuário e atendimento")}</li>
                <li>{t("legal.privacy.s3.i5", "Cumprimento de obrigações legais e regulatórias")}</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                4. {t("legal.privacy.s4.title", "Base legal (LGPD)")}
              </h2>
              <p>
                {t("legal.privacy.s4.body", "Tratamos seus dados com base em: (i) execução do contrato firmado com sua empresa, (ii) cumprimento de obrigação legal, (iii) legítimo interesse para segurança e prevenção de fraude, e (iv) consentimento, quando aplicável.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                5. {t("legal.privacy.s5.title", "Compartilhamento")}
              </h2>
              <p>
                {t("legal.privacy.s5.body", "Não vendemos seus dados. Compartilhamos apenas com prestadores de serviço essenciais (provedor de infraestrutura, e-mail transacional, processador de pagamento) sob contrato de confidencialidade, e quando exigido por lei.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                6. {t("legal.privacy.s6.title", "Cookies")}
              </h2>
              <p>
                {t("legal.privacy.s6.body", "Usamos apenas cookies essenciais para autenticação, preferências de tema e sessão. Não usamos cookies de rastreamento de terceiros nem ferramentas de analytics que identifiquem usuários.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                7. {t("legal.privacy.s7.title", "Seus direitos (LGPD Art. 18)")}
              </h2>
              <p className="mb-2">{t("legal.privacy.s7.intro", "Como titular dos dados, você pode a qualquer momento:")}</p>
              <ul className="list-disc list-inside space-y-1.5">
                <li>{t("legal.privacy.s7.i1", "Confirmar a existência de tratamento")}</li>
                <li>{t("legal.privacy.s7.i2", "Acessar seus dados")}</li>
                <li>{t("legal.privacy.s7.i3", "Corrigir dados incompletos, inexatos ou desatualizados")}</li>
                <li>{t("legal.privacy.s7.i4", "Solicitar anonimização, bloqueio ou eliminação")}</li>
                <li>{t("legal.privacy.s7.i5", "Solicitar portabilidade")}</li>
                <li>{t("legal.privacy.s7.i6", "Revogar consentimento")}</li>
              </ul>
              <p className="mt-3">
                {t("legal.privacy.s7.contact", "Para exercer qualquer direito, contate")}{" "}
                <a href="mailto:dpo@millersolutions.com.br" className="text-app-1 underline hover:opacity-80">
                  dpo@millersolutions.com.br
                </a>
                .
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                8. {t("legal.privacy.s8.title", "Segurança")}
              </h2>
              <p>
                {t("legal.privacy.s8.body", "Aplicamos medidas técnicas e organizacionais para proteger seus dados: criptografia em trânsito (HTTPS) e em repouso, controle de acesso por papel (RBAC), auditoria de operações sensíveis e backup automático.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                9. {t("legal.privacy.s9.title", "Retenção")}
              </h2>
              <p>
                {t("legal.privacy.s9.body", "Mantemos seus dados pelo tempo necessário ao cumprimento das finalidades descritas, ou pelo prazo legal aplicável. Após esse período, os dados são anonimizados ou eliminados.")}
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl font-semibold text-app-1 mb-3">
                10. {t("legal.privacy.s10.title", "Alterações")}
              </h2>
              <p>
                {t("legal.privacy.s10.body", "Esta Política pode ser atualizada. Versões anteriores ficam disponíveis mediante solicitação. Alterações materiais serão comunicadas por e-mail.")}
              </p>
            </section>

            <section className="pt-6 border-t border-app-subtle">
              <p className="text-xs text-app-3">
                {t("legal.privacy.placeholder", "Este é um documento preliminar conforme a LGPD (Lei 13.709/2018). A versão definitiva será publicada após revisão jurídica e indicação formal do Encarregado de Dados (DPO).")}
              </p>
            </section>
          </div>

          <div className="mt-12 pt-6 border-t border-app-subtle flex items-center gap-4 text-xs text-app-3">
            <Link href="/termos" className="hover:text-app-2 underline underline-offset-2">
              {t("legal.termsLink", "Termos de Uso")}
            </Link>
            <span>·</span>
            <Link href="/" className="hover:text-app-2 underline underline-offset-2">
              {t("legal.homeLink", "Início")}
            </Link>
          </div>
        </main>
      </div>
    </>
  );
}
